import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { NumericFormat } from 'react-number-format';
import {
  FaFileContract, FaPlus, FaEdit, FaTrash, FaPrint, FaSearch, FaSave, FaTimes,
  FaArrowLeft, FaUser, FaCar, FaMoneyBillWave, FaExclamationTriangle, FaChevronLeft, FaChevronRight, FaCog
} from 'react-icons/fa';
import Header from '../others/Header';
import config from '../config/config';
import { useToast } from '../components/ToastContext';
import ConfirmModal from '../components/ConfirmModal';
import ImportarDocumento from '../components/ImportarDocumento';
import AvisoCadastroIncompleto from '../components/AvisoCadastroIncompleto';
import { resolverAnexo } from '../others/importacao';
import { anexarDocumentoCliente, anexarDocumentoVeiculo } from '../others/anexos';
import ModalConfiguracaoModelo from '../components/ModalConfiguracaoModelo';

const { API_BASE_URL } = config;

// ══════════════════════════════════════════════════════════════
// MODELOS DE CONTRATO
// ══════════════════════════════════════════════════════════════
// Comodato, locação e arrendamento usam o mesmo texto; só muda a nomenclatura.
const TIPO_RESERVA = 'RESERVA_DOMINIO';
const TIPOS_USO = ['COMODATO', 'LOCACAO', 'ARRENDAMENTO'];
const NOMES_TIPO = {
  RESERVA_DOMINIO: 'Compra e Venda com Reserva de Domínio',
  COMODATO: 'Comodato',
  LOCACAO: 'Locação',
  ARRENDAMENTO: 'Arrendamento'
};
// Modelos disponíveis pra personalização de texto em Configurações (mesma lista de NOMES_TIPO)
const MODELOS_CONFIGURACAO = Object.entries(NOMES_TIPO).map(([tipo, titulo]) => ({ tipo, titulo }));
const TOKENS_CONTRATO = [
  'PAPEL_A', 'PAPEL_B', 'VALOR_TOTAL', 'VALOR_ENTRADA', 'VALOR_PARCELA', 'QUANTIDADE_PARCELAS',
  'TAXA_JUROS', 'PERCENTUAL_MULTA', 'DATA_PRIMEIRA_PARCELA', 'DATA_ULTIMA_PARCELA',
  'VALOR_MENSAL', 'DIA_VENCIMENTO', 'DATA_INICIO', 'DATA_FIM', 'CIDADE_CONTRATO', 'UF_CONTRATO'
];

const PAPEIS = {
  RESERVA_DOMINIO: ['Vendedor', 'Comprador'],
  COMODATO: ['Comodante', 'Comodatário'],
  LOCACAO: ['Locador', 'Locatário'],
  ARRENDAMENTO: ['Arrendante', 'Arrendatário']
};

// ══════════════════════════════════════════════════════════════
// AUXILIARES
// ══════════════════════════════════════════════════════════════
// Datas sempre como 'AAAA-MM-DD' pela data LOCAL (toISOString() usa UTC e, à noite, devolveria o dia seguinte)
function hojeISO() {
  const h = new Date();
  return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, '0')}-${String(h.getDate()).padStart(2, '0')}`;
}

function apenasDigitos(valor) {
  return (valor || '').toString().replace(/\D/g, '');
}

function arredondar2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// Soma meses a uma data 'AAAA-MM-DD' mantendo o dia (31/01 + 1 mês = 28/02 ou 29/02)
function somarMeses(iso, meses) {
  if (!iso) return '';
  const [ano, mes, dia] = iso.split('-').map(Number);
  const alvo = new Date(ano, mes - 1 + meses, 1);
  const ultimoDia = new Date(alvo.getFullYear(), alvo.getMonth() + 1, 0).getDate();
  const d = Math.min(dia, ultimoDia);
  return `${alvo.getFullYear()}-${String(alvo.getMonth() + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function formatarReais(valor) {
  if (valor === null || valor === undefined || isNaN(valor)) return 'R$ 0,00';
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatarDataBR(iso) {
  if (!iso) return '-';
  const [ano, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${ano}`;
}

// Campos que podem ser gravados no cadastro a partir do contrato
const CAMPOS_PARTE = ['nome', 'cpfCnpj', 'cep', 'logradouro', 'numero', 'complemento', 'bairro', 'cidade', 'uf', 'telefone', 'email'];
const CAMPOS_VEICULO = ['veiculoPlaca', 'veiculoChassi', 'veiculoRenavam', 'veiculoMarcaModelo', 'veiculoAno', 'veiculoCor'];
const ROTULOS_CAMPO = {
  nome: 'nome', cpfCnpj: 'CPF/CNPJ', cep: 'CEP', logradouro: 'logradouro', numero: 'número', complemento: 'complemento',
  bairro: 'bairro', cidade: 'cidade', uf: 'UF', telefone: 'telefone', email: 'e-mail',
  veiculoPlaca: 'placa', veiculoChassi: 'chassi', veiculoRenavam: 'RENAVAM', veiculoMarcaModelo: 'marca/modelo', veiculoAno: 'ano', veiculoCor: 'cor'
};

// Normaliza para comparar com o cadastro: CPF/CNPJ só dígitos; UF e chassi em maiúsculas; sem espaços nas pontas.
// Mesma regra de others/comparacaoCadastro.js (aqui os campos têm outro nome, por isso a cópia): a placa
// também ignora o hífen, senão um contrato com ABC-1D23 contra um cadastro gravado ABC1D23 apontaria
// "placa diferente" sendo a mesma placa.
function normalizar(campo, valor) {
  const v = (valor ?? '').toString().trim();
  if (campo === 'cpfCnpj') return apenasDigitos(v);
  if (campo === 'veiculoPlaca') return v.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (['uf', 'veiculoChassi'].includes(campo)) return v.toUpperCase();
  return v;
}

// Campos preenchidos no contrato que são novos ou diferentes do cadastro. Campo em branco não conta (não apaga nada).
function camposDiferentes(atual, origem, campos) {
  return campos.filter((c) => {
    const novo = normalizar(c, atual[c]);
    return novo !== '' && novo !== normalizar(c, origem ? origem[c] : '');
  });
}

// Situação dos dados em relação ao cadastro: 'vazio' | 'novo' | 'alterado' | 'igual' | 'desconhecido' (contrato reaberto)
function situacaoCadastro({ vinculado, origem, atual, campos, obrigatorios, rotuloNovo, camposFixos = [] }) {
  if (!vinculado) {
    const completo = obrigatorios.every((c) => normalizar(c, atual[c]) !== '');
    return completo ? { tipo: 'novo', texto: rotuloNovo } : { tipo: 'vazio', texto: 'Preencha os dados para poder salvá-los no cadastro.' };
  }
  if (!origem) return { tipo: 'desconhecido', texto: 'Atualiza o cadastro com os dados preenchidos aqui (campos em branco não apagam nada).' };
  const diferentes = camposDiferentes(atual, origem, campos.filter((c) => !camposFixos.includes(c)));
  if (diferentes.length === 0) return { tipo: 'igual', texto: 'Dados iguais aos do cadastro.' };
  return { tipo: 'alterado', texto: `Novo ou diferente do cadastro: ${diferentes.map((c) => ROTULOS_CAMPO[c]).join(', ')}.` };
}

// Dados de um cliente do cadastro no formato de parte do contrato
function parteDoCliente(c) {
  return {
    idCliente: c.idCliente,
    nome: c.nome || '',
    cpfCnpj: apenasDigitos(c.cpfCnpj),
    cep: c.cep || '', logradouro: c.logradouro || '', numero: c.numero || '', complemento: c.complemento || '',
    bairro: c.bairro || '', cidade: c.cidade || '', uf: c.uf || '',
    telefone: c.telefone || '', email: c.email || ''
  };
}

// Dados de um veículo do cadastro no formato do contrato
function veiculoDoCadastro(v) {
  return {
    idVeiculo: v.idVeiculo ?? null,
    veiculoPlaca: v.placa || '', veiculoChassi: v.chassi || '', veiculoRenavam: v.renavam || '',
    veiculoMarcaModelo: v.marcaModelo || '', veiculoAno: v.ano || '', veiculoCor: v.cor || ''
  };
}

const parteVazia = () => ({
  idCliente: null, nome: '', cpfCnpj: '', cep: '', logradouro: '', numero: '', complemento: '',
  bairro: '', cidade: '', uf: '', telefone: '', email: ''
});

const formVazio = () => ({
  idContrato: null,
  tipo: TIPO_RESERVA,
  parteA: parteVazia(),
  parteB: parteVazia(),
  idVeiculo: null,
  veiculoPlaca: '', veiculoChassi: '', veiculoRenavam: '', veiculoMarcaModelo: '', veiculoAno: '', veiculoCor: '',
  cidadeContrato: '', ufContrato: '', dataContrato: hojeISO(),
  // reserva de domínio
  valorTotal: null, temEntrada: false, valorEntrada: null, quantidadeParcelas: 1, valorParcela: null,
  dataPrimeiraParcela: '', dataUltimaParcela: '', temJuros: false, taxaJurosMes: null, temMulta: false, percentualMulta: null,
  // comodato / locação / arrendamento
  temCusto: false, valorMensal: null, diaVencimento: '', prazoIndeterminado: false,
  dataInicio: hojeISO(), dataFim: '', incluirTestemunhas: true
});

// Contrato vindo do back -> estado do formulário (nulos viram vazio)
function paraForm(c) {
  const base = formVazio();
  const parte = (p) => ({ ...parteVazia(), ...Object.fromEntries(Object.entries(p || {}).map(([k, v]) => [k, v ?? (k === 'idCliente' ? null : '')])) });
  const form = { ...base };
  Object.keys(base).forEach((k) => {
    if (k === 'parteA' || k === 'parteB') return;
    const v = c[k];
    if (v === null || v === undefined) return;
    form[k] = v;
  });
  form.parteA = parte(c.parteA);
  form.parteB = parte(c.parteB);
  form.diaVencimento = c.diaVencimento ?? '';
  return form;
}

function montarPayload(f) {
  const vazioParaNulo = (v) => (v === '' || v === undefined ? null : v);
  const p = { ...f };
  ['dataPrimeiraParcela', 'dataUltimaParcela', 'dataInicio', 'dataFim', 'dataContrato'].forEach((k) => { p[k] = vazioParaNulo(p[k]); });
  p.diaVencimento = f.diaVencimento === '' ? null : Number(f.diaVencimento);
  p.quantidadeParcelas = f.tipo === TIPO_RESERVA ? Number(f.quantidadeParcelas) : null;
  return p;
}

// Mesmas regras do back; devolve { 'campo': 'mensagem' }
function validarForm(f) {
  const e = {};
  const obrig = (chave, valor, msg = 'Este campo é obrigatório') => { if (!valor || !String(valor).trim()) e[chave] = msg; };
  const [papelA, papelB] = PAPEIS[f.tipo];

  ['parteA', 'parteB'].forEach((k) => {
    obrig(`${k}.nome`, f[k].nome);
    obrig(`${k}.cpfCnpj`, f[k].cpfCnpj);
    obrig(`${k}.logradouro`, f[k].logradouro);
    obrig(`${k}.cidade`, f[k].cidade);
  });
  if (f.parteA.cpfCnpj && f.parteB.cpfCnpj && apenasDigitos(f.parteA.cpfCnpj) === apenasDigitos(f.parteB.cpfCnpj)) {
    e['parteB.cpfCnpj'] = `${papelA} e ${papelB} não podem ser a mesma pessoa`;
  }

  obrig('veiculoPlaca', f.veiculoPlaca);
  obrig('veiculoChassi', f.veiculoChassi);
  obrig('veiculoMarcaModelo', f.veiculoMarcaModelo);
  obrig('cidadeContrato', f.cidadeContrato);
  obrig('ufContrato', f.ufContrato);
  if (f.ufContrato && f.ufContrato.trim().length !== 2) e.ufContrato = 'Use 2 letras (ex.: RS)';
  obrig('dataContrato', f.dataContrato);

  if (f.tipo === TIPO_RESERVA) {
    const total = f.valorTotal || 0;
    const entrada = f.temEntrada ? (f.valorEntrada || 0) : 0;
    const n = Number(f.quantidadeParcelas) || 0;
    const parcela = f.valorParcela || 0;
    const restante = total - entrada;
    if (!(total > 0)) e.valorTotal = 'Valor deve ser maior que zero';
    if (f.temEntrada) {
      if (!(entrada > 0)) e.valorEntrada = 'Valor deve ser maior que zero';
      else if (total > 0 && entrada >= total) e.valorEntrada = 'A entrada deve ser menor que o valor total';
    }
    if (!(n >= 1 && n <= 999)) e.quantidadeParcelas = 'Informe de 1 a 999';
    if (!(parcela > 0)) e.valorParcela = 'Valor deve ser maior que zero';
    obrig('dataPrimeiraParcela', f.dataPrimeiraParcela);
    if (n > 1) {
      obrig('dataUltimaParcela', f.dataUltimaParcela);
      if (f.dataUltimaParcela && f.dataPrimeiraParcela && f.dataUltimaParcela < f.dataPrimeiraParcela) {
        e.dataUltimaParcela = 'A última parcela não pode vencer antes da primeira';
      }
    }
    if (f.temJuros) {
      if (!(f.taxaJurosMes > 0)) e.taxaJurosMes = 'Informe a taxa ao mês';
      else if (f.taxaJurosMes > 100) e.taxaJurosMes = 'Não pode passar de 100%';
    }
    if (f.temMulta) {
      if (!(f.percentualMulta > 0)) e.percentualMulta = 'Informe o percentual';
      else if (f.percentualMulta > 100) e.percentualMulta = 'Não pode passar de 100%';
    }
    if (!e.valorTotal && !e.valorEntrada && !e.quantidadeParcelas && !e.valorParcela) {
      const soma = parcela * n;
      const tolerancia = 0.01 * n;
      if (f.temJuros) {
        if (soma + tolerancia < restante) e.valorParcela = `Com juros, as parcelas somam menos que o restante (${formatarReais(restante)})`;
      } else if (Math.abs(soma - restante) > tolerancia) {
        e.valorParcela = `Sem juros, as parcelas somam ${formatarReais(soma)}, mas o restante é ${formatarReais(restante)}`;
      }
    }
  } else {
    obrig('dataInicio', f.dataInicio);
    if (!f.prazoIndeterminado) {
      obrig('dataFim', f.dataFim);
      if (f.dataFim && f.dataInicio && f.dataFim < f.dataInicio) e.dataFim = 'O término não pode ser antes do início';
    }
    if (f.temCusto) {
      if (!(f.valorMensal > 0)) e.valorMensal = 'Valor deve ser maior que zero';
      const dia = Number(f.diaVencimento);
      if (!(dia >= 1 && dia <= 31)) e.diaVencimento = 'Informe de 1 a 31';
    }
  }
  return e;
}

// ══════════════════════════════════════════════════════════════
// TELA PRINCIPAL: lista <-> formulário
// ══════════════════════════════════════════════════════════════
const Contratos = () => {
  const [modo, setModo] = useState('lista'); // 'lista' | 'form'
  const [idEditando, setIdEditando] = useState(null);
  const [modalConfigAberto, setModalConfigAberto] = useState(false);

  const buscarTextoPadraoContrato = async (tipo) => {
    const resp = await axios.get(`${API_BASE_URL}/contratos/preview-texto`, {
      ...authHeader(),
      params: { tipo }
    });
    return resp.data;
  };

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.content}>
        <div style={styles.header}>
          <FaFileContract style={styles.titleIcon} />
          <h1 style={styles.title}>Contratos</h1>
          <button
            type="button"
            onClick={() => setModalConfigAberto(true)}
            style={styles.configButton}
            title="Personalizar texto dos modelos de contrato"
          >
            <FaCog /> Configurações
          </button>
        </div>
        {modalConfigAberto && (
          <ModalConfiguracaoModelo
            categoria="CONTRATO"
            modelos={MODELOS_CONFIGURACAO}
            tokensAjuda={TOKENS_CONTRATO}
            buscarTextoPadrao={buscarTextoPadraoContrato}
            onClose={() => setModalConfigAberto(false)}
          />
        )}
        {modo === 'lista' ? (
          <ListaContratos
            onNovo={() => { setIdEditando(null); setModo('form'); }}
            onEditar={(id) => { setIdEditando(id); setModo('form'); }}
          />
        ) : (
          <FormContrato idInicial={idEditando} onVoltar={() => setModo('lista')} />
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// LISTA DE CONTRATOS
// ══════════════════════════════════════════════════════════════
// GET    /contratos?busca=&page=&size=  -> página de resumos (mais recentes primeiro)
// GET    /contratos/{id}/pdf            -> PDF do contrato (blob)
// DELETE /contratos/{id}
const ListaContratos = ({ onNovo, onEditar }) => {
  const [busca, setBusca] = useState('');
  const [termo, setTermo] = useState('');
  const [pagina, setPagina] = useState(0);
  const [dados, setDados] = useState({ content: [], totalPages: 0, totalElements: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recarga, setRecarga] = useState(0);
  const [excluir, setExcluir] = useState(null);
  const { addToast } = useToast();

  // espera o usuário parar de digitar antes de buscar
  useEffect(() => {
    const t = setTimeout(() => { setTermo(busca); setPagina(0); }, 400);
    return () => clearTimeout(t);
  }, [busca]);

  useEffect(() => {
    let cancelado = false;
    const carregar = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API_BASE_URL}/contratos`, {
          ...authHeader(),
          params: { busca: termo || undefined, page: pagina, size: 10 }
        });
        if (!cancelado) setDados(response.data);
      } catch (err) {
        if (!cancelado) {
          console.error('Erro ao carregar contratos:', err);
          setError(err.response?.data?.message || 'Erro ao carregar contratos');
        }
      } finally {
        if (!cancelado) setLoading(false);
      }
    };
    carregar();
    return () => { cancelado = true; };
  }, [termo, pagina, recarga]);

  const imprimir = async (id) => {
    try {
      await abrirPdf(id);
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
      addToast('error', 'Erro ao gerar o PDF do contrato');
    }
  };

  const confirmarExclusao = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/contratos/${excluir.idContrato}`, authHeader());
      addToast('success', 'Contrato excluído com sucesso!');
      setRecarga((r) => r + 1);
    } catch (err) {
      console.error('Erro ao excluir contrato:', err);
      addToast('error', err.response?.data?.message || 'Erro ao excluir contrato');
    } finally {
      setExcluir(null);
    }
  };

  return (
    <>
      {error && <div style={styles.errorMessage}>{error}</div>}

      <div style={styles.painel}>
        <div style={styles.listaTopo}>
          <div style={styles.buscaBox}>
            <FaSearch style={styles.buscaIcone} />
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nome, placa ou chassi"
              style={styles.buscaInput}
            />
          </div>
          <button onClick={onNovo} style={styles.primaryButton}><FaPlus /> Novo Contrato</button>
        </div>

        {loading ? (
          <p style={styles.loading}>Carregando...</p>
        ) : dados.content.length > 0 ? (
          <div style={styles.tabelaWrapper}>
            <table style={styles.tabela}>
              <thead>
                <tr>
                  <th style={styles.th}>Tipo</th>
                  <th style={styles.th}>Partes</th>
                  <th style={styles.th}>Veículo</th>
                  <th style={styles.th}>Data</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {dados.content.map((c) => (
                  <tr key={c.idContrato} style={styles.tr}>
                    <td style={styles.td}><span style={styles.tag}>{c.tipoDescricao}</span></td>
                    <td style={styles.td}>
                      <div>{c.parteANome}</div>
                      <div style={styles.subTexto}>{c.parteBNome}</div>
                    </td>
                    <td style={styles.td}>{c.veiculoPlaca}</td>
                    <td style={styles.td}>{formatarDataBR(c.dataContrato)}</td>
                    <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button title="Imprimir PDF" aria-label={`Imprimir contrato ${c.idContrato}`} onClick={() => imprimir(c.idContrato)} style={styles.iconButton}><FaPrint /></button>{' '}
                      <button title="Editar" aria-label={`Editar contrato ${c.idContrato}`} onClick={() => onEditar(c.idContrato)} style={styles.iconButton}><FaEdit /></button>{' '}
                      <button title="Excluir" aria-label={`Excluir contrato ${c.idContrato}`} onClick={() => setExcluir(c)} style={{ ...styles.iconButton, ...styles.iconButtonPerigo }}><FaTrash /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={styles.noResults}>{termo ? 'Nenhum contrato encontrado para esta busca' : 'Nenhum contrato emitido ainda'}</p>
        )}

        {dados.totalPages > 1 && (
          <div style={styles.paginacao}>
            <button style={styles.navButton} disabled={pagina === 0} onClick={() => setPagina((p) => p - 1)} aria-label="Página anterior"><FaChevronLeft /></button>
            <span style={styles.paginaTexto}>Página {pagina + 1} de {dados.totalPages}</span>
            <button style={styles.navButton} disabled={pagina + 1 >= dados.totalPages} onClick={() => setPagina((p) => p + 1)} aria-label="Próxima página"><FaChevronRight /></button>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={!!excluir}
        onClose={() => setExcluir(null)}
        onConfirm={confirmarExclusao}
        title="Excluir contrato"
        message={excluir ? `Tem certeza que deseja excluir o contrato de ${excluir.tipoDescricao} de ${excluir.parteANome}?` : ''}
        confirmText="Excluir"
        cancelText="Cancelar"
      />
    </>
  );
};

// ══════════════════════════════════════════════════════════════
// FORMULÁRIO
// ══════════════════════════════════════════════════════════════
// GET  /contratos/{id}         -> contrato para edição
// POST /contratos              -> cria; PUT /contratos/{id} -> atualiza
// GET  /clientes/cpfcnpj/{doc} e /clientes/nome/{nome} -> busca de clientes; GET /clientes/{id} -> dados completos e veículos
// GET  /veiculos/placa/{placa} -> busca de veículo
const FormContrato = ({ idInicial, onVoltar }) => {
  const [form, setForm] = useState(formVazio);
  const [erros, setErros] = useState({});
  const [carregando, setCarregando] = useState(!!idInicial);
  const [salvando, setSalvando] = useState(false);
  const [veiculosDaParteA, setVeiculosDaParteA] = useState([]);
  // sugestões automáticas só valem enquanto o usuário não editou o campo à mão
  const [parcelaManual, setParcelaManual] = useState(false);
  const [ultimaManual, setUltimaManual] = useState(false);
  // dados como estão no cadastro (para saber o que é novo ou diferente); vazio quando o contrato foi reaberto
  const [origem, setOrigem] = useState({ parteA: null, parteB: null, veiculo: null });
  const [salvandoCadastro, setSalvandoCadastro] = useState(null); // 'parteA' | 'parteB' | 'veiculo'
  const [confirmacao, setConfirmacao] = useState(null);
  // Documentos (CNH/RG) importados para cada parte; ficam fora do form porque o form inteiro vai para o back ao salvar o contrato
  const [importacoes, setImportacoes] = useState({ parteA: null, parteB: null });
  const [importacaoVeiculo, setImportacaoVeiculo] = useState(null); // CRLV/ATPV importado (a ser anexado ao veículo ao salvar)
  const { addToast } = useToast();

  const ehReserva = form.tipo === TIPO_RESERVA;
  const [papelA, papelB] = PAPEIS[form.tipo];

  useEffect(() => {
    if (!idInicial) return;
    let cancelado = false;
    (async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/contratos/${idInicial}`, authHeader());
        if (cancelado) return;
        setForm(paraForm(response.data));
        setParcelaManual(true);
        setUltimaManual(true);
      } catch (err) {
        console.error('Erro ao carregar contrato:', err);
        addToast('error', 'Erro ao carregar o contrato');
        onVoltar();
      } finally {
        if (!cancelado) setCarregando(false);
      }
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idInicial]);

  // Valor da parcela sugerido: (total - entrada) / parcelas, só sem juros
  useEffect(() => {
    if (!ehReserva || parcelaManual || form.temJuros) return;
    const total = form.valorTotal || 0;
    const entrada = form.temEntrada ? (form.valorEntrada || 0) : 0;
    const n = Number(form.quantidadeParcelas) || 0;
    if (total > 0 && n > 0 && total > entrada) {
      const sugerido = arredondar2((total - entrada) / n);
      setForm((f) => (f.valorParcela === sugerido ? f : { ...f, valorParcela: sugerido }));
    }
  }, [ehReserva, parcelaManual, form.temJuros, form.valorTotal, form.temEntrada, form.valorEntrada, form.quantidadeParcelas]);

  // Data da última parcela sugerida: primeira + (parcelas - 1) meses
  useEffect(() => {
    if (!ehReserva || ultimaManual || !form.dataPrimeiraParcela) return;
    const n = Number(form.quantidadeParcelas) || 1;
    const sugerida = n <= 1 ? form.dataPrimeiraParcela : somarMeses(form.dataPrimeiraParcela, n - 1);
    setForm((f) => (f.dataUltimaParcela === sugerida ? f : { ...f, dataUltimaParcela: sugerida }));
  }, [ehReserva, ultimaManual, form.dataPrimeiraParcela, form.quantidadeParcelas]);

  const setCampo = (campo, valor) => {
    setForm((f) => ({ ...f, [campo]: valor }));
    setErros((e) => (e[campo] ? { ...e, [campo]: undefined } : e));
  };
  const setParte = (parte, campo, valor) => {
    setForm((f) => ({ ...f, [parte]: { ...f[parte], [campo]: valor } }));
    setErros((e) => (e[`${parte}.${campo}`] ? { ...e, [`${parte}.${campo}`]: undefined } : e));
  };

  // ── Importar dados de CNH/RG e guardar o arquivo no cadastro do cliente ──
  const aplicarDocumentoParte = (chave, dados) => {
    Object.entries(dados).forEach(([campo, valor]) => setParte(chave, campo, valor));
  };

  const anexarImportacao = async (chave, idCliente, cpfSalvo) => {
    const { arquivo, divergente } = resolverAnexo(importacoes[chave], cpfSalvo);
    if (divergente) {
      addToast('warning', 'O documento importado é de outro CPF/CNPJ; não foi anexado a este cliente. Anexe-o pelos documentos do cliente, se for o caso.');
      setImportacoes((imp) => ({ ...imp, [chave]: null }));
      return;
    }
    if (!arquivo) return;
    const anexou = await anexarDocumentoCliente(API_BASE_URL, idCliente, arquivo, authHeader());
    addToast(
      anexou ? 'success' : 'warning',
      anexou ? 'Documento guardado nos documentos do cliente.' : 'Cadastro salvo, mas não foi possível guardar o documento. Anexe-o pelos documentos do cliente.'
    );
    setImportacoes((imp) => ({ ...imp, [chave]: null }));
  };


  // ── Importar dados do CRLV/ATPV e guardar o arquivo no cadastro do veículo ──
  const anexarImportacaoVeiculo = async (idVeiculo, placaSalva) => {
    const { arquivo, divergente } = resolverAnexo(importacaoVeiculo, placaSalva);
    setImportacaoVeiculo(null);
    if (divergente) {
      addToast('warning', 'O documento importado é de outra placa; não foi anexado a este veículo. Anexe-o pelos documentos do veículo, se for o caso.');
      return;
    }
    if (!arquivo) return;
    const anexou = await anexarDocumentoVeiculo(API_BASE_URL, idVeiculo, arquivo, authHeader());
    addToast(
      anexou ? 'success' : 'warning',
      anexou ? 'Documento guardado nos documentos do veículo.' : 'Veículo salvo, mas não foi possível guardar o documento. Anexe-o pelos documentos do veículo.'
    );
  };

  const aplicarDocumentoVeiculo = (dados) => {
    const campos = { placa: 'veiculoPlaca', renavam: 'veiculoRenavam', chassi: 'veiculoChassi', marcaModelo: 'veiculoMarcaModelo', cor: 'veiculoCor', ano: 'veiculoAno' };
    Object.entries(dados).forEach(([campo, valor]) => setCampo(campos[campo], valor));
  };

  const escolherTipo = (tipo) => {
    setForm((f) => ({ ...f, tipo }));
    setErros({});
  };

  const aoSelecionarCliente = (parte, dadosParte, cliente) => {
    setForm((f) => {
      const novo = { ...f, [parte]: dadosParte };
      // cidade/UF do contrato: sugere a cidade da primeira parte quando ainda não foi preenchida
      if (parte === 'parteA' && !f.cidadeContrato && dadosParte.cidade) {
        novo.cidadeContrato = dadosParte.cidade;
        novo.ufContrato = dadosParte.uf || f.ufContrato;
      }
      return novo;
    });
    setOrigem((o) => ({ ...o, [parte]: dadosParte }));
    if (parte === 'parteA') setVeiculosDaParteA(Array.isArray(cliente.veiculos) ? cliente.veiculos : []);
    setErros((e) => Object.fromEntries(Object.entries(e).filter(([k]) => !k.startsWith(`${parte}.`))));
  };

  const aoSelecionarVeiculo = (v) => {
    setImportacaoVeiculo(null);
    setForm((f) => ({
      ...f,
      idVeiculo: v.idVeiculo ?? null,
      veiculoPlaca: v.placa || '',
      veiculoChassi: v.chassi || '',
      veiculoRenavam: v.renavam || '',
      veiculoMarcaModelo: v.marcaModelo || '',
      veiculoAno: v.ano || '',
      veiculoCor: v.cor || ''
    }));
    setErros((e) => Object.fromEntries(Object.entries(e).filter(([k]) => !k.startsWith('veiculo'))));
    setOrigem((o) => ({ ...o, veiculo: veiculoDoCadastro(v) }));
    if (!v.chassi) addToast('warning', 'Este veículo não tem chassi cadastrado. Informe o chassi e use "Salvar dados do veículo" para guardá-lo no cadastro.');
  };

  // ── Gravar no cadastro (clientes e veículo) ─────────────────────────────
  const aplicarClienteSalvo = (chave, cliente) => {
    const dados = parteDoCliente(cliente);
    setForm((f) => ({ ...f, [chave]: dados }));
    setOrigem((o) => ({ ...o, [chave]: dados }));
    if (chave === 'parteA' && Array.isArray(cliente.veiculos)) setVeiculosDaParteA(cliente.veiculos);
  };

  const complementarCliente = async (chave, idCliente) => {
    setSalvandoCadastro(chave);
    try {
      const p = form[chave];
      const response = await axios.put(`${API_BASE_URL}/clientes/${idCliente}/complementar`, {
        nome: p.nome, cpfCnpj: apenasDigitos(p.cpfCnpj), cep: p.cep, logradouro: p.logradouro, numero: p.numero,
        complemento: p.complemento, bairro: p.bairro, cidade: p.cidade, uf: p.uf, telefone: p.telefone, email: p.email
      }, authHeader());
      const jaCadastrado = origem[chave] ? origem[chave].cpfCnpj : response.data.cpfCnpj;
      if (apenasDigitos(p.cpfCnpj) && apenasDigitos(p.cpfCnpj) !== apenasDigitos(jaCadastrado)) {
        addToast('warning', 'O CPF/CNPJ do cadastro não é alterado por aqui. Para mudá-lo, use a tela de Clientes.');
      }
      aplicarClienteSalvo(chave, response.data);
      addToast('success', 'Cadastro do cliente atualizado com sucesso!');
      await anexarImportacao(chave, idCliente, response.data.cpfCnpj);
    } catch (err) {
      console.error('Erro ao atualizar cliente:', err);
      addToast('error', err.response?.data?.message || 'Erro ao salvar os dados do cliente');
    } finally {
      setSalvandoCadastro(null);
    }
  };

  const salvarClienteNoCadastro = async (chave) => {
    const p = form[chave];
    if (!p.nome.trim() || !apenasDigitos(p.cpfCnpj)) {
      addToast('warning', 'Informe ao menos o nome e o CPF/CNPJ para salvar no cadastro.');
      return;
    }
    if (p.idCliente) {
      await complementarCliente(chave, p.idCliente);
      return;
    }
    setSalvandoCadastro(chave);
    try {
      const existente = await buscarClientePorDocumento(p.cpfCnpj);
      if (existente) {
        setConfirmacao({
          titulo: 'Cliente já cadastrado',
          mensagem: `Já existe um cliente com este CPF/CNPJ: ${existente.nome}. Atualizar o cadastro dele com os dados preenchidos aqui?`,
          textoConfirmar: 'Atualizar',
          onConfirm: () => complementarCliente(chave, existente.idCliente)
        });
        return;
      }
      const response = await axios.post(`${API_BASE_URL}/clientes`, {
        nome: p.nome.trim(), cpfCnpj: apenasDigitos(p.cpfCnpj), ativo: true, cep: p.cep, logradouro: p.logradouro, numero: p.numero,
        complemento: p.complemento, bairro: p.bairro, cidade: p.cidade, uf: p.uf, telefone: p.telefone, email: p.email
      }, authHeader());
      aplicarClienteSalvo(chave, response.data);
      addToast('success', 'Cliente cadastrado com sucesso!');
      await anexarImportacao(chave, response.data.idCliente, response.data.cpfCnpj);
    } catch (err) {
      console.error('Erro ao cadastrar cliente:', err);
      addToast('error', err.response?.data?.message || 'Erro ao cadastrar o cliente');
    } finally {
      setSalvandoCadastro(null);
    }
  };

  const aplicarVeiculoSalvo = (veiculo) => {
    const dados = veiculoDoCadastro(veiculo);
    setForm((f) => ({ ...f, ...dados }));
    setOrigem((o) => ({ ...o, veiculo: dados }));
    if (form.parteA.idCliente) {
      // atualiza a lista de veículos do cliente com o veículo recém-salvo
      axios.get(`${API_BASE_URL}/clientes/${form.parteA.idCliente}`, authHeader())
        .then((r) => setVeiculosDaParteA(Array.isArray(r.data.veiculos) ? r.data.veiculos : []))
        .catch(() => {});
    }
  };

  const complementarVeiculo = async (idVeiculo) => {
    setSalvandoCadastro('veiculo');
    try {
      const response = await axios.put(`${API_BASE_URL}/veiculos/${idVeiculo}/complementar`, {
        placa: form.veiculoPlaca.trim(), chassi: form.veiculoChassi, renavam: form.veiculoRenavam,
        marcaModelo: form.veiculoMarcaModelo, ano: form.veiculoAno, cor: form.veiculoCor
      }, authHeader());
      aplicarVeiculoSalvo(response.data);
      addToast('success', 'Cadastro do veículo atualizado com sucesso!');
      await anexarImportacaoVeiculo(response.data.idVeiculo || idVeiculo, response.data.placa);
    } catch (err) {
      console.error('Erro ao atualizar veículo:', err);
      addToast('error', err.response?.data?.message || 'Erro ao salvar os dados do veículo');
    } finally {
      setSalvandoCadastro(null);
    }
  };

  const salvarVeiculoNoCadastro = async () => {
    const placa = form.veiculoPlaca.trim();
    if (!placa || !form.veiculoMarcaModelo.trim()) {
      addToast('warning', 'Informe ao menos a placa e a marca/modelo para salvar no cadastro.');
      return;
    }
    if (form.idVeiculo) {
      await complementarVeiculo(form.idVeiculo);
      return;
    }
    setSalvandoCadastro('veiculo');
    try {
      const existente = await buscarVeiculoPorPlaca(placa);
      if (existente) {
        setConfirmacao({
          titulo: 'Veículo já cadastrado',
          mensagem: `Já existe um veículo com esta placa: ${existente.marcaModelo || existente.placa}${existente.nomeCliente ? ` (cliente ${existente.nomeCliente})` : ''}. Atualizar o cadastro dele com os dados preenchidos aqui?`,
          textoConfirmar: 'Atualizar',
          onConfirm: () => complementarVeiculo(existente.idVeiculo)
        });
        return;
      }
      if (!form.parteA.idCliente) {
        addToast('warning', `Salve primeiro os dados do ${papelA.toLowerCase()} no cadastro: o veículo fica vinculado ao cliente.`);
        return;
      }
      const response = await axios.post(`${API_BASE_URL}/veiculos`, {
        placa, marcaModelo: form.veiculoMarcaModelo.trim(), cor: form.veiculoCor, ano: form.veiculoAno,
        renavam: form.veiculoRenavam, chassi: form.veiculoChassi, cliente: { idCliente: form.parteA.idCliente }
      }, authHeader());
      aplicarVeiculoSalvo(response.data);
      addToast('success', 'Veículo cadastrado com sucesso!');
      await anexarImportacaoVeiculo(response.data.idVeiculo, response.data.placa);
    } catch (err) {
      console.error('Erro ao cadastrar veículo:', err);
      addToast('error', err.response?.data?.message || 'Erro ao cadastrar o veículo');
    } finally {
      setSalvandoCadastro(null);
    }
  };

  const salvar = async (gerarPdf) => {
    const e = validarForm(form);
    setErros(e);
    const chaves = Object.keys(e);
    if (chaves.length > 0) {
      addToast('warning', e[chaves[0]] === 'Este campo é obrigatório' ? 'Preencha todos os campos obrigatórios corretamente!' : e[chaves[0]]);
      return;
    }
    try {
      setSalvando(true);
      const payload = montarPayload(form);
      const response = form.idContrato
        ? await axios.put(`${API_BASE_URL}/contratos/${form.idContrato}`, payload, authHeader())
        : await axios.post(`${API_BASE_URL}/contratos`, payload, authHeader());
      const salvo = response.data;
      setForm((f) => ({ ...f, idContrato: salvo.idContrato }));
      addToast('success', form.idContrato ? 'Contrato atualizado com sucesso!' : 'Contrato salvo com sucesso!');
      if (gerarPdf) {
        await abrirPdf(salvo.idContrato);
      }
    } catch (err) {
      console.error('Erro ao salvar contrato:', err);
      addToast('error', err.response?.data?.message || 'Erro ao salvar contrato');
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) return <div style={styles.painel}><p style={styles.loading}>Carregando contrato...</p></div>;

  const situacaoParte = (chave) => situacaoCadastro({
    vinculado: !!form[chave].idCliente, origem: origem[chave], atual: form[chave], campos: CAMPOS_PARTE,
    obrigatorios: ['nome', 'cpfCnpj'], rotuloNovo: 'Cliente ainda não cadastrado: será criado no cadastro.', camposFixos: ['cpfCnpj']
  });
  const situacaoVeiculo = situacaoCadastro({
    vinculado: !!form.idVeiculo, origem: origem.veiculo, atual: form, campos: CAMPOS_VEICULO,
    obrigatorios: ['veiculoPlaca', 'veiculoMarcaModelo'], rotuloNovo: 'Veículo ainda não cadastrado: será criado no cadastro do cliente.', camposFixos: ['veiculoPlaca']
  });

  const restante = (form.valorTotal || 0) - (form.temEntrada ? (form.valorEntrada || 0) : 0);
  const somaParcelas = (form.valorParcela || 0) * (Number(form.quantidadeParcelas) || 0);

  return (
    <>
      <button onClick={onVoltar} style={styles.linkVoltar}><FaArrowLeft /> Voltar para a lista</button>

      {/* ══════════ MODELO ══════════ */}
      <div style={styles.painel}>
        <h3 style={styles.painelTitulo}>Modelo do contrato</h3>
        <div style={styles.cardsModelo}>
          <button
            type="button"
            onClick={() => escolherTipo(TIPO_RESERVA)}
            style={{ ...styles.cardModelo, ...(ehReserva && styles.cardModeloAtivo) }}
          >
            <div style={styles.cardModeloTitulo}>Compra e Venda com Reserva de Domínio</div>
            <div style={styles.cardModeloSub}>Veículo usado, com entrada, parcelas, juros e multa opcionais</div>
          </button>
          <button
            type="button"
            onClick={() => escolherTipo(ehReserva ? 'COMODATO' : form.tipo)}
            style={{ ...styles.cardModelo, ...(!ehReserva && styles.cardModeloAtivo) }}
          >
            <div style={styles.cardModeloTitulo}>Comodato / Locação / Arrendamento</div>
            <div style={styles.cardModeloSub}>Uso do veículo (ex.: para registro do contrato com a ANTT)</div>
          </button>
        </div>
        {!ehReserva && (
          <div style={styles.nomenclatura}>
            <span style={styles.label}>Nomenclatura do contrato:</span>
            {TIPOS_USO.map((t) => (
              <label key={t} style={styles.radioLabel}>
                <input type="radio" name="tipoUso" checked={form.tipo === t} onChange={() => escolherTipo(t)} /> {NOMES_TIPO[t]}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* ══════════ PARTES ══════════ */}
      {/* Cliente vindo da importação de CRLV-e nasce só com nome e CPF: avisa o que falta para o contrato */}
      <AvisoCadastroIncompleto
        documento="contrato"
        pessoas={[{ rotulo: papelA, pessoa: form.parteA }, { rotulo: papelB, pessoa: form.parteB }]}
      />
      <ParteForm
        titulo={papelA} chave="parteA" parte={form.parteA} erros={erros}
        onCampo={setParte} onSelecionarCliente={aoSelecionarCliente} addToast={addToast}
        importacao={importacoes.parteA} onImportacaoChange={(imp) => setImportacoes((i) => ({ ...i, parteA: imp }))} onDadosLidos={(dados) => aplicarDocumentoParte('parteA', dados)}
        situacao={situacaoParte('parteA')} salvandoCadastro={salvandoCadastro === 'parteA'} onSalvarCadastro={() => salvarClienteNoCadastro('parteA')}
      />
      <ParteForm
        titulo={papelB} chave="parteB" parte={form.parteB} erros={erros}
        onCampo={setParte} onSelecionarCliente={aoSelecionarCliente} addToast={addToast}
        importacao={importacoes.parteB} onImportacaoChange={(imp) => setImportacoes((i) => ({ ...i, parteB: imp }))} onDadosLidos={(dados) => aplicarDocumentoParte('parteB', dados)}
        situacao={situacaoParte('parteB')} salvandoCadastro={salvandoCadastro === 'parteB'} onSalvarCadastro={() => salvarClienteNoCadastro('parteB')}
      />

      {/* ══════════ VEÍCULO ══════════ */}
      <VeiculoForm
        form={form} erros={erros} veiculosDaParteA={veiculosDaParteA}
        onCampo={setCampo} onSelecionarVeiculo={aoSelecionarVeiculo} addToast={addToast}
        importacao={importacaoVeiculo} onImportacaoChange={setImportacaoVeiculo} onDadosLidos={aplicarDocumentoVeiculo}
        situacao={situacaoVeiculo} salvandoCadastro={salvandoCadastro === 'veiculo'} onSalvarCadastro={salvarVeiculoNoCadastro}
      />

      {/* ══════════ CONDIÇÕES ══════════ */}
      {ehReserva ? (
        <div style={styles.painel}>
          <h3 style={styles.painelTitulo}><FaMoneyBillWave style={styles.painelIcone} /> Pagamento</h3>

          <div style={styles.formRow}>
            <Campo label="Valor total do veículo" obrigatorio erro={erros.valorTotal}>
              <CampoValor value={form.valorTotal} onChange={(v) => setCampo('valorTotal', v)} erro={erros.valorTotal} />
            </Campo>
            <Campo label="Houve valor de entrada?">
              <SimNao valor={form.temEntrada} onChange={(v) => setCampo('temEntrada', v)} />
            </Campo>
            {form.temEntrada && (
              <Campo label="Valor da entrada" obrigatorio erro={erros.valorEntrada}>
                <CampoValor value={form.valorEntrada} onChange={(v) => setCampo('valorEntrada', v)} erro={erros.valorEntrada} />
              </Campo>
            )}
          </div>

          <div style={styles.formRow}>
            <Campo label="Quantidade de parcelas" obrigatorio erro={erros.quantidadeParcelas}>
              <input
                type="number" min="1" max="999" value={form.quantidadeParcelas}
                onChange={(e) => setCampo('quantidadeParcelas', e.target.value === '' ? '' : Number(e.target.value))}
                style={{ ...styles.input, ...(erros.quantidadeParcelas && styles.inputError) }}
              />
            </Campo>
            <Campo label="Valor de cada parcela" obrigatorio erro={erros.valorParcela}>
              <CampoValor
                value={form.valorParcela}
                onChange={(v) => { setParcelaManual(true); setCampo('valorParcela', v); }}
                erro={erros.valorParcela}
              />
              {parcelaManual && !form.temJuros && (
                <button type="button" style={styles.linkPequeno} onClick={() => setParcelaManual(false)}>Sugerir valor automaticamente</button>
              )}
            </Campo>
          </div>

          <div style={styles.formRow}>
            <Campo label="Vencimento da primeira parcela" obrigatorio erro={erros.dataPrimeiraParcela}>
              <input
                type="date" value={form.dataPrimeiraParcela}
                onChange={(e) => setCampo('dataPrimeiraParcela', e.target.value)}
                style={{ ...styles.input, ...(erros.dataPrimeiraParcela && styles.inputError) }}
              />
            </Campo>
            <Campo label="Vencimento da última parcela" obrigatorio erro={erros.dataUltimaParcela}>
              <input
                type="date" value={form.dataUltimaParcela} disabled={Number(form.quantidadeParcelas) <= 1}
                onChange={(e) => { setUltimaManual(true); setCampo('dataUltimaParcela', e.target.value); }}
                style={{ ...styles.input, ...(erros.dataUltimaParcela && styles.inputError) }}
              />
              {ultimaManual && Number(form.quantidadeParcelas) > 1 && (
                <button type="button" style={styles.linkPequeno} onClick={() => setUltimaManual(false)}>Sugerir data automaticamente</button>
              )}
            </Campo>
          </div>

          <div style={styles.formRow}>
            <Campo label="Cobrar juros?">
              <SimNao valor={form.temJuros} onChange={(v) => setCampo('temJuros', v)} />
            </Campo>
            {form.temJuros && (
              <Campo label="Juros ao mês" obrigatorio erro={erros.taxaJurosMes}>
                <CampoPercentual value={form.taxaJurosMes} onChange={(v) => setCampo('taxaJurosMes', v)} erro={erros.taxaJurosMes} />
              </Campo>
            )}
            <Campo label="Cobrar multa por atraso?">
              <SimNao valor={form.temMulta} onChange={(v) => setCampo('temMulta', v)} />
            </Campo>
            {form.temMulta && (
              <Campo label="Multa (% sobre a parcela em atraso)" obrigatorio erro={erros.percentualMulta}>
                <CampoPercentual value={form.percentualMulta} onChange={(v) => setCampo('percentualMulta', v)} erro={erros.percentualMulta} />
              </Campo>
            )}
          </div>
          {form.temJuros && (
            <p style={styles.dica}>Com juros, informe o valor da parcela já com os juros incluídos: o contrato registra a taxa e o valor que você digitar.</p>
          )}

          {form.valorTotal > 0 && Number(form.quantidadeParcelas) > 0 && form.valorParcela > 0 && (
            <div style={styles.resumoBox}>
              <span>Restante a pagar: <strong>{formatarReais(restante)}</strong></span>
              <span>{form.quantidadeParcelas} parcela(s) de {formatarReais(form.valorParcela)} = <strong>{formatarReais(somaParcelas)}</strong></span>
            </div>
          )}
        </div>
      ) : (
        <div style={styles.painel}>
          <h3 style={styles.painelTitulo}><FaMoneyBillWave style={styles.painelIcone} /> Condições — {NOMES_TIPO[form.tipo]}</h3>

          <div style={styles.formRow}>
            <Campo label="Custo mensal">
              <SimNao valor={form.temCusto} onChange={(v) => setCampo('temCusto', v)} rotuloSim="Com custo" rotuloNao="Gratuito" />
            </Campo>
            {form.temCusto && (
              <>
                <Campo label="Valor mensal" obrigatorio erro={erros.valorMensal}>
                  <CampoValor value={form.valorMensal} onChange={(v) => setCampo('valorMensal', v)} erro={erros.valorMensal} />
                </Campo>
                <Campo label="Dia do vencimento" obrigatorio erro={erros.diaVencimento}>
                  <input
                    type="number" min="1" max="31" value={form.diaVencimento}
                    onChange={(e) => setCampo('diaVencimento', e.target.value)}
                    style={{ ...styles.input, ...(erros.diaVencimento && styles.inputError) }}
                  />
                </Campo>
              </>
            )}
          </div>
          {form.temCusto && form.tipo === 'COMODATO' && (
            <div style={styles.aviso}>
              <FaExclamationTriangle /> <span>O comodato é, por definição, um empréstimo gratuito. Com valor mensal, o instrumento passa a ser, na prática, uma locação. Considere usar a nomenclatura "Locação".</span>
            </div>
          )}

          <div style={styles.formRow}>
            <Campo label="Prazo">
              <SimNao valor={!form.prazoIndeterminado} onChange={(v) => setCampo('prazoIndeterminado', !v)} rotuloSim="Determinado" rotuloNao="Indeterminado" />
            </Campo>
            <Campo label="Início da vigência" obrigatorio erro={erros.dataInicio}>
              <input
                type="date" value={form.dataInicio} onChange={(e) => setCampo('dataInicio', e.target.value)}
                style={{ ...styles.input, ...(erros.dataInicio && styles.inputError) }}
              />
            </Campo>
            {!form.prazoIndeterminado && (
              <Campo label="Término da vigência" obrigatorio erro={erros.dataFim}>
                <input
                  type="date" value={form.dataFim} onChange={(e) => setCampo('dataFim', e.target.value)}
                  style={{ ...styles.input, ...(erros.dataFim && styles.inputError) }}
                />
              </Campo>
            )}
          </div>

          <label style={styles.checkLabel}>
            <input type="checkbox" checked={form.incluirTestemunhas} onChange={(e) => setCampo('incluirTestemunhas', e.target.checked)} />
            Incluir linhas para duas testemunhas
          </label>
        </div>
      )}

      {/* ══════════ LOCAL E DATA ══════════ */}
      <div style={styles.painel}>
        <h3 style={styles.painelTitulo}>Local e data do contrato</h3>
        <div style={styles.formRow}>
          <Campo label="Cidade (também é o foro)" obrigatorio erro={erros.cidadeContrato}>
            <input
              type="text" value={form.cidadeContrato} onChange={(e) => setCampo('cidadeContrato', e.target.value)}
              style={{ ...styles.input, ...(erros.cidadeContrato && styles.inputError) }}
            />
          </Campo>
          <Campo label="UF" obrigatorio erro={erros.ufContrato} pequeno>
            <input
              type="text" maxLength={2} value={form.ufContrato} onChange={(e) => setCampo('ufContrato', e.target.value.toUpperCase())}
              style={{ ...styles.input, ...(erros.ufContrato && styles.inputError) }}
            />
          </Campo>
          <Campo label="Data do contrato" obrigatorio erro={erros.dataContrato}>
            <input
              type="date" value={form.dataContrato} onChange={(e) => setCampo('dataContrato', e.target.value)}
              style={{ ...styles.input, ...(erros.dataContrato && styles.inputError) }}
            />
          </Campo>
        </div>
      </div>

      <div style={styles.acoes}>
        <button onClick={onVoltar} style={styles.secondaryButton} disabled={salvando}><FaTimes /> Cancelar</button>
        <button onClick={() => salvar(false)} style={styles.secondaryButtonTeal} disabled={salvando}><FaSave /> Salvar</button>
        <button onClick={() => salvar(true)} style={styles.primaryButton} disabled={salvando}>
          <FaPrint /> {salvando ? 'Salvando...' : 'Salvar e gerar PDF'}
        </button>
      </div>

      <ConfirmModal
        isOpen={!!confirmacao}
        onClose={() => setConfirmacao(null)}
        onConfirm={() => { if (confirmacao) confirmacao.onConfirm(); }}
        title={confirmacao ? confirmacao.titulo : ''}
        message={confirmacao ? confirmacao.mensagem : ''}
        confirmText={confirmacao ? confirmacao.textoConfirmar : 'Confirmar'}
        cancelText="Cancelar"
      />
    </>
  );
};

// ══════════════════════════════════════════════════════════════
// PARTE DO CONTRATO (busca no cadastro de clientes + dados editáveis)
// ══════════════════════════════════════════════════════════════
const ParteForm = ({ titulo, chave, parte, erros, onCampo, onSelecionarCliente, addToast, importacao, onImportacaoChange, onDadosLidos, situacao, salvandoCadastro, onSalvarCadastro }) => {
  const [termo, setTermo] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [buscou, setBuscou] = useState(false);

  const buscar = async () => {
    const t = termo.trim();
    if (t.length < 3) {
      addToast('warning', 'Informe ao menos 3 caracteres para pesquisar.');
      return;
    }
    setBuscando(true);
    setBuscou(false);
    setResultados([]);
    try {
      try {
        const respDoc = await axios.get(`${API_BASE_URL}/clientes/cpfcnpj/${apenasDigitos(t)}`, authHeader());
        if (apenasDigitos(t).length >= 3 && respDoc.data) {
          setResultados([respDoc.data]);
          return;
        }
      } catch (err) {
        // não achou por CPF/CNPJ: tenta por nome
      }
      const respNome = await axios.get(`${API_BASE_URL}/clientes/nome/${encodeURIComponent(t.toLowerCase())}`, authHeader());
      if (respNome.data && respNome.data.length > 0) setResultados(respNome.data);
    } catch (err) {
      console.error('Erro na pesquisa de clientes:', err);
      addToast('error', 'Erro ao pesquisar clientes.');
    } finally {
      setBuscando(false);
      setBuscou(true);
    }
  };

  const selecionar = async (resumo) => {
    setBuscando(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/clientes/${resumo.idCliente}`, authHeader());
      const c = response.data;
      onSelecionarCliente(chave, parteDoCliente(c), c);
      setResultados([]);
      setTermo('');
      setBuscou(false);
    } catch (err) {
      console.error('Erro ao carregar cliente:', err);
      addToast('error', 'Erro ao carregar os dados do cliente');
    } finally {
      setBuscando(false);
    }
  };

  const campo = (nome, rotulo, opcoes = {}) => (
    <Campo label={rotulo} obrigatorio={opcoes.obrigatorio} erro={erros[`${chave}.${nome}`]} pequeno={opcoes.pequeno}>
      <input
        type="text" aria-label={`${titulo} ${rotulo}`} value={parte[nome]} maxLength={opcoes.max}
        onChange={(e) => onCampo(chave, nome, opcoes.maiusculas ? e.target.value.toUpperCase() : e.target.value)}
        style={{ ...styles.input, ...(erros[`${chave}.${nome}`] && styles.inputError) }}
      />
    </Campo>
  );

  return (
    <div style={styles.painel}>
      <h3 style={styles.painelTitulo}><FaUser style={styles.painelIcone} /> {titulo}</h3>

      <ImportarDocumento importacao={importacao} onImportacaoChange={onImportacaoChange} onDadosLidos={onDadosLidos} campos={['nome', 'cpfCnpj']} />

      <div style={styles.buscaCliente}>
        <input
          type="text" value={termo} placeholder={`Buscar ${titulo.toLowerCase()} no cadastro (nome ou CPF/CNPJ)`}
          aria-label={`Buscar ${titulo}`}
          onChange={(e) => setTermo(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') buscar(); }}
          style={styles.inputBusca}
        />
        <button type="button" onClick={buscar} style={styles.secondaryButtonTeal} disabled={buscando}>
          <FaSearch /> {buscando ? 'Buscando...' : 'Buscar'}
        </button>
      </div>
      {resultados.length > 0 && (
        <div style={styles.resultados}>
          {resultados.map((r) => (
            <button type="button" key={r.idCliente} style={styles.resultadoItem} onClick={() => selecionar(r)}>
              <strong>{r.nome}</strong> <span style={styles.subTexto}>{r.cpfCnpj}</span>
            </button>
          ))}
        </div>
      )}
      {buscou && resultados.length === 0 && <p style={styles.dica}>Nenhum cliente encontrado. Você pode preencher os dados abaixo manualmente.</p>}

      <div style={styles.formRow}>
        {campo('nome', 'Nome', { obrigatorio: true })}
        {campo('cpfCnpj', 'CPF/CNPJ', { obrigatorio: true })}
      </div>
      <div style={styles.formRow}>
        {campo('logradouro', 'Logradouro', { obrigatorio: true })}
        {campo('numero', 'Número', { pequeno: true })}
        {campo('complemento', 'Complemento')}
      </div>
      <div style={styles.formRow}>
        {campo('bairro', 'Bairro')}
        {campo('cidade', 'Cidade', { obrigatorio: true })}
        {campo('uf', 'UF', { pequeno: true, max: 2, maiusculas: true })}
        {campo('cep', 'CEP', { pequeno: true })}
      </div>
      <div style={styles.formRow}>
        {campo('telefone', 'Telefone')}
        {campo('email', 'E-mail')}
      </div>

      <BarraCadastro situacao={situacao} salvando={salvandoCadastro} onSalvar={onSalvarCadastro} rotulo="Salvar dados do cliente" />
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// VEÍCULO (apenas um por contrato)
// ══════════════════════════════════════════════════════════════
const VeiculoForm = ({ form, erros, veiculosDaParteA, onCampo, onSelecionarVeiculo, addToast, importacao, onImportacaoChange, onDadosLidos, situacao, salvandoCadastro, onSalvarCadastro }) => {
  const [placaBusca, setPlacaBusca] = useState('');
  const [buscando, setBuscando] = useState(false);

  const buscarPlaca = async () => {
    const placa = placaBusca.trim().toUpperCase();
    if (placa.length < 6) {
      addToast('warning', 'Informe a placa completa para pesquisar.');
      return;
    }
    setBuscando(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/veiculos/placa/${encodeURIComponent(placa)}`, authHeader());
      if (response.data) {
        onSelecionarVeiculo(response.data);
        setPlacaBusca('');
      } else {
        addToast('warning', 'Veículo não encontrado no cadastro. Preencha os dados abaixo manualmente.');
      }
    } catch (err) {
      addToast('warning', 'Veículo não encontrado no cadastro. Preencha os dados abaixo manualmente.');
    } finally {
      setBuscando(false);
    }
  };

  const campo = (nome, rotulo, opcoes = {}) => (
    <Campo label={rotulo} obrigatorio={opcoes.obrigatorio} erro={erros[nome]} pequeno={opcoes.pequeno}>
      <input
        type="text" aria-label={`Veículo ${rotulo}`} value={form[nome]}
        onChange={(e) => onCampo(nome, opcoes.maiusculas ? e.target.value.toUpperCase() : e.target.value)}
        style={{ ...styles.input, ...(erros[nome] && styles.inputError) }}
      />
    </Campo>
  );

  return (
    <div style={styles.painel}>
      <h3 style={styles.painelTitulo}><FaCar style={styles.painelIcone} /> Veículo</h3>

      <ImportarDocumento tipo="veiculo" importacao={importacao} onImportacaoChange={onImportacaoChange} onDadosLidos={onDadosLidos} />

      <div style={styles.buscaCliente}>
        {veiculosDaParteA.length > 0 && (
          <select
            aria-label="Veículos do cliente" style={styles.inputBusca} value=""
            onChange={(e) => { const v = veiculosDaParteA.find((x) => String(x.idVeiculo) === e.target.value); if (v) onSelecionarVeiculo(v); }}
          >
            <option value="">Escolher entre os veículos do cliente...</option>
            {veiculosDaParteA.map((v) => (
              <option key={v.idVeiculo} value={v.idVeiculo}>{v.placa} - {v.marcaModelo}</option>
            ))}
          </select>
        )}
        <input
          type="text" value={placaBusca} placeholder="Ou buscar pela placa" aria-label="Buscar placa"
          onChange={(e) => setPlacaBusca(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === 'Enter') buscarPlaca(); }}
          style={styles.inputBusca}
        />
        <button type="button" onClick={buscarPlaca} style={styles.secondaryButtonTeal} disabled={buscando}>
          <FaSearch /> {buscando ? 'Buscando...' : 'Buscar'}
        </button>
      </div>

      <div style={styles.formRow}>
        {campo('veiculoPlaca', 'Placa', { obrigatorio: true, maiusculas: true })}
        {campo('veiculoChassi', 'Chassi', { obrigatorio: true, maiusculas: true })}
        {campo('veiculoRenavam', 'RENAVAM')}
      </div>
      <div style={styles.formRow}>
        {campo('veiculoMarcaModelo', 'Marca/Modelo', { obrigatorio: true })}
        {campo('veiculoAno', 'Ano', { pequeno: true })}
        {campo('veiculoCor', 'Cor')}
      </div>

      <BarraCadastro situacao={situacao} salvando={salvandoCadastro} onSalvar={onSalvarCadastro} rotulo="Salvar dados do veículo" />
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// BARRA "SALVAR NO CADASTRO" (cliente ou veículo)
// ══════════════════════════════════════════════════════════════
// PUT /clientes/{id}/complementar e PUT /veiculos/{id}/complementar: só campos preenchidos sobrescrevem, nada é apagado.
// POST /clientes e POST /veiculos criam o registro quando ainda não existe.
const BarraCadastro = ({ situacao, salvando, onSalvar, rotulo }) => {
  const destaque = situacao.tipo === 'novo' || situacao.tipo === 'alterado';
  return (
    <div style={styles.barraCadastro}>
      <span style={{ ...styles.barraCadastroTexto, ...(destaque && styles.barraCadastroTextoDestaque) }}>{situacao.texto}</span>
      <button
        type="button" onClick={onSalvar}
        disabled={salvando || situacao.tipo === 'vazio' || situacao.tipo === 'igual'}
        style={{
          ...(destaque ? styles.primaryButton : styles.secondaryButtonTeal),
          ...((salvando || situacao.tipo === 'vazio' || situacao.tipo === 'igual') && styles.botaoDesabilitado)
        }}
      >
        <FaSave /> {salvando ? 'Salvando...' : rotulo}
      </button>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// CAMPOS REUTILIZÁVEIS
// ══════════════════════════════════════════════════════════════
// O rótulo do campo também vira o aria-label do primeiro elemento (input, select ou campo formatado) que ele envolve
const Campo = ({ label, obrigatorio, erro, pequeno, children }) => (
  <div style={{ ...styles.inputGroup, ...(pequeno && styles.inputGroupPequeno) }}>
    <label style={styles.label}>{label}{obrigatorio ? '*' : ''}</label>
    {React.Children.map(children, (filho, i) => (
      i === 0 && React.isValidElement(filho) && filho.props['aria-label'] === undefined
        ? React.cloneElement(filho, { 'aria-label': label })
        : filho
    ))}
    {erro && <span style={styles.errorText}>{erro}</span>}
  </div>
);

const SimNao = ({ valor, onChange, rotuloSim = 'Sim', rotuloNao = 'Não', 'aria-label': ariaLabel }) => (
  <div style={styles.simNao} role="group" aria-label={ariaLabel}>
    <button type="button" onClick={() => onChange(true)} aria-pressed={valor} style={{ ...styles.simNaoBtn, ...(valor && styles.simNaoAtivo) }}>{rotuloSim}</button>
    <button type="button" onClick={() => onChange(false)} aria-pressed={!valor} style={{ ...styles.simNaoBtn, ...(!valor && styles.simNaoAtivo) }}>{rotuloNao}</button>
  </div>
);

// Só reage a digitação do usuário (source 'event'), para as sugestões automáticas não dispararem o onChange
const CampoValor = ({ value, onChange, erro, 'aria-label': ariaLabel }) => (
  <NumericFormat
    value={value ?? ''}
    onValueChange={(v, info) => { if (info.source === 'event') onChange(v.floatValue ?? null); }}
    thousandSeparator="." decimalSeparator="," decimalScale={2} fixedDecimalScale
    placeholder="R$ 0,00" prefix="R$ " aria-label={ariaLabel}
    style={{ ...styles.input, ...(erro && styles.inputError) }}
  />
);

const CampoPercentual = ({ value, onChange, erro, 'aria-label': ariaLabel }) => (
  <NumericFormat
    value={value ?? ''}
    onValueChange={(v, info) => { if (info.source === 'event') onChange(v.floatValue ?? null); }}
    decimalSeparator="," thousandSeparator="." decimalScale={2} suffix=" %" placeholder="0 %" aria-label={ariaLabel}
    style={{ ...styles.input, ...(erro && styles.inputError) }}
  />
);

// ══════════════════════════════════════════════════════════════
// REQUISIÇÕES
// ══════════════════════════════════════════════════════════════
function authHeader() {
  const token = localStorage.getItem('jwtToken');
  return { headers: { Authorization: `Bearer ${token}` } };
}

// Cliente do cadastro com este CPF/CNPJ (ou null)
async function buscarClientePorDocumento(documento) {
  try {
    const response = await axios.get(`${API_BASE_URL}/clientes/cpfcnpj/${apenasDigitos(documento)}`, authHeader());
    return response.data && response.data.idCliente ? response.data : null;
  } catch (err) {
    return null;
  }
}

// Veículo do cadastro com esta placa (ou null). Tenta a placa como digitada, com hífen e sem hífen.
async function buscarVeiculoPorPlaca(placa) {
  const digitada = placa.trim().toUpperCase();
  const semHifen = digitada.replace(/-/g, '');
  const comHifen = semHifen.length >= 7 ? `${semHifen.slice(0, 3)}-${semHifen.slice(3)}` : digitada;
  for (const variante of [...new Set([digitada, comHifen, semHifen])]) {
    try {
      const response = await axios.get(`${API_BASE_URL}/veiculos/placa/${encodeURIComponent(variante)}`, authHeader());
      if (response.data && response.data.idVeiculo) return response.data;
    } catch (err) {
      // tenta a próxima variante
    }
  }
  return null;
}

async function abrirPdf(id) {
  const response = await axios.get(`${API_BASE_URL}/contratos/${id}/pdf`, { ...authHeader(), responseType: 'blob' });
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const novaJanela = window.open(url, '_blank');
  if (!novaJanela) {
    const link = document.createElement('a');
    link.href = url;
    link.download = `contrato-${id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

// ══════════════════════════════════════════════════════════════
// ESTILOS — paleta Nexus Despachante (#004d4d / #008080 / #20b2aa)
// ══════════════════════════════════════════════════════════════
const TEAL_DARK = '#004d4d';
const TEAL = '#008080';
const TEAL_LIGHT_ACCENT = '#20b2aa';

const styles = {
  container: {
    display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f5f5f5', color: '#333',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  },
  content: { padding: '24px', maxWidth: '1100px', margin: '0 auto', width: '100%', boxSizing: 'border-box' },
  header: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' },
  titleIcon: { fontSize: '26px', color: TEAL },
  title: { fontSize: '22px', margin: 0, color: TEAL_DARK, fontWeight: '700', flex: 1 },
  configButton: {
    display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 16px',
    backgroundColor: 'white', color: TEAL_DARK, border: `1.5px solid ${TEAL}`,
    borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
  },
  errorMessage: { backgroundColor: '#ffebee', color: '#c62828', padding: '12px', borderRadius: '4px', marginBottom: '20px' },

  painel: {
    backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
    marginBottom: '20px', boxSizing: 'border-box'
  },
  painelTitulo: {
    fontSize: '16px', margin: '0 0 16px 0', color: TEAL_DARK, display: 'flex', alignItems: 'center', fontWeight: '700',
    borderBottom: `2px solid ${TEAL}22`, paddingBottom: '10px'
  },
  painelIcone: { marginRight: '8px', color: TEAL },
  linkVoltar: {
    background: 'none', border: 'none', color: TEAL, cursor: 'pointer', fontSize: '14px', fontWeight: '600',
    display: 'flex', alignItems: 'center', gap: '6px', padding: 0, marginBottom: '14px'
  },

  // Lista
  listaTopo: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' },
  buscaBox: { position: 'relative', flex: '1 1 260px', maxWidth: '420px' },
  buscaIcone: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#95a5a6' },
  buscaInput: { width: '100%', padding: '10px 12px 10px 36px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' },
  tabelaWrapper: { overflowX: 'auto' },
  tabela: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: `2px solid ${TEAL}22`, color: TEAL_DARK, fontWeight: '700', fontSize: '13px' },
  tr: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '10px 12px', verticalAlign: 'middle' },
  tag: { backgroundColor: '#e0f2f1', color: TEAL_DARK, padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' },
  subTexto: { fontSize: '12px', color: '#7f8c8d' },
  iconButton: {
    width: '32px', height: '32px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 'none',
    borderRadius: '6px', backgroundColor: '#eaf6f6', color: TEAL, cursor: 'pointer'
  },
  iconButtonPerigo: { backgroundColor: '#fdecea', color: '#c0392b' },
  paginacao: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '14px', marginTop: '16px' },
  paginaTexto: { fontSize: '13px', color: '#7f8c8d' },
  navButton: {
    width: '34px', height: '34px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 'none',
    borderRadius: '6px', backgroundColor: '#eaf6f6', color: TEAL, cursor: 'pointer'
  },
  loading: { textAlign: 'center', padding: '30px', color: '#7f8c8d' },
  noResults: { textAlign: 'center', padding: '30px', color: '#95a5a6', fontStyle: 'italic' },

  // Modelo
  cardsModelo: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px' },
  cardModelo: {
    textAlign: 'left', backgroundColor: 'white', padding: '16px', borderRadius: '10px', border: '2px solid #e5e7eb',
    cursor: 'pointer', fontFamily: 'inherit'
  },
  cardModeloAtivo: { border: `2px solid ${TEAL}`, backgroundColor: '#f0fdfa' },
  cardModeloTitulo: { fontSize: '15px', fontWeight: '700', color: TEAL_DARK },
  cardModeloSub: { fontSize: '12px', color: '#7f8c8d', marginTop: '4px' },
  nomenclatura: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '18px', marginTop: '16px' },
  radioLabel: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' },

  // Formulário
  formRow: { display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '4px' },
  inputGroup: { flex: '1 1 200px', minWidth: 0, marginBottom: '14px' },
  inputGroupPequeno: { flex: '0 1 130px' },
  label: { display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#34495e' },
  input: { width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit' },
  inputError: { border: '1px solid #ff4444' },
  // campo de busca: ocupa o espaço livre e deixa o botão "Buscar" na mesma linha
  inputBusca: { flex: '1 1 240px', minWidth: 0, padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit' },
  errorText: { color: '#ff4444', fontSize: '12px', marginTop: '4px', display: 'block' },
  dica: { fontSize: '12px', color: '#7f8c8d', margin: '0 0 12px 0' },
  linkPequeno: { background: 'none', border: 'none', color: TEAL, cursor: 'pointer', fontSize: '11px', padding: '4px 0 0 0', textDecoration: 'underline' },
  buscaCliente: { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' },
  resultados: { border: '1px solid #e5e7eb', borderRadius: '8px', marginBottom: '14px', overflow: 'hidden' },
  resultadoItem: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', width: '100%', padding: '10px 12px',
    border: 'none', borderBottom: '1px solid #f0f0f0', backgroundColor: 'white', cursor: 'pointer', textAlign: 'left', fontSize: '14px', fontFamily: 'inherit'
  },
  simNao: { display: 'inline-flex', border: `1px solid ${TEAL}`, borderRadius: '6px', overflow: 'hidden' },
  simNaoBtn: { padding: '9px 16px', border: 'none', backgroundColor: 'white', color: TEAL, cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: 'inherit' },
  simNaoAtivo: { backgroundColor: TEAL, color: 'white' },
  checkLabel: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer', marginTop: '6px' },
  resumoBox: {
    display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', backgroundColor: '#f0fdfa',
    border: `1px solid ${TEAL_LIGHT_ACCENT}55`, borderRadius: '8px', padding: '12px 16px', fontSize: '14px', color: TEAL_DARK, marginTop: '6px'
  },
  aviso: {
    display: 'flex', alignItems: 'flex-start', gap: '10px', backgroundColor: '#fff8e1', border: '1px solid #ffe082',
    borderRadius: '8px', padding: '12px 14px', fontSize: '13px', color: '#8a6d00', marginBottom: '14px'
  },

  barraCadastro: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px',
    marginTop: '4px', paddingTop: '14px', borderTop: '1px dashed #e5e7eb'
  },
  barraCadastroTexto: { fontSize: '12px', color: '#7f8c8d', flex: '1 1 260px' },
  barraCadastroTextoDestaque: { color: '#8a6d00', fontWeight: '600' },
  botaoDesabilitado: { opacity: 0.55, cursor: 'not-allowed' },
  acoes: { display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap', marginBottom: '30px' },
  primaryButton: {
    padding: '10px 16px', backgroundColor: TEAL, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600'
  },
  secondaryButton: {
    padding: '10px 16px', backgroundColor: '#95a5a6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600'
  },
  secondaryButtonTeal: {
    padding: '10px 16px', backgroundColor: '#eaf6f6', color: TEAL_DARK, border: `1px solid ${TEAL}55`, borderRadius: '6px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600'
  }
};

export default Contratos;
